import type { CanvasNodeRow } from './canvas-nodes';
import { ApiClient } from './client';
import type { ApiResponse } from './types';
import { translateClient } from '@/lib/i18n/client';

export type VideoReplicationReferenceRole = 'auto' | 'person' | 'garment' | 'outfit' | 'object' | 'scene';
export type GenjutsuMode = 'motion-transfer' | 'object-swap' | 'restyle';
export type GenjutsuVideoModel = `higgsfield/genjutsu/${GenjutsuMode}/v1.0`;
export type VideoReplicationModel = 'seedance-2.0' | 'seedance-2.5' | 'heygen-video-1' | 'kling-v4-omni' | GenjutsuVideoModel;
export interface GenjutsuPreset { id: string; name: string; preview_url: string }
export interface GenjutsuQuote { credits: number; billable_seconds: number; resolution: string; video_model: GenjutsuVideoModel }
interface KagemaneQuoteIdentity {
  video_model: VideoReplicationModel;
  resolution: string;
  gift_applied?: boolean;
  gift_discount_credits?: number;
  gift_covered_seconds?: number;
}
export type KagemaneQuote = KagemaneQuoteIdentity & ({
  credits: number;
  estimate_min: number;
  estimate_max: number;
  pricing_mode: 'fixed' | 'range';
  billable_seconds: number;
} | {
  pricing_mode: 'unplanned';
  credits: null;
  estimate_min: null;
  estimate_max: null;
  billable_seconds: null;
  source_duration_sec: number;
});
export type KagemaneBillingMode = 'dobi' | 'monthly_gift';
export interface KagemaneGifts {
  monthly_gift_limit: number;
  monthly_gifts_remaining: number;
  gift_resets_at: string | null;
  model: 'selected' | 'seedance-2.0';
  models?: VideoReplicationModel[];
  resolutions?: string[];
  resolution: '1080p';
  max_duration_sec: number;
  max_source_duration_sec?: number;
}

export interface CreateVideoReplicationRequest {
  project_id?: string | null;
  entry_point?: 'kagemane';
  client_request_id?: string;
  billing_mode?: KagemaneBillingMode;
  gift_max_credits?: number;
  source_video_url?: string;
  /** Public library reference; resolved by the server through the same admission as an upload. */
  source_catalog_id?: string;
  source_node_id?: string | null;
  source_asset_id?: string | null;
  /** Frontend media timing is sent in milliseconds and converted at the API boundary. */
  source_duration_ms?: number | null;
  reference_image_urls: string[];
  reference_asset_ids?: Array<string | null>;
  reference_roles?: VideoReplicationReferenceRole[];
  instruction?: string | null;
  resolution?: '480p' | '768p' | '720p' | '1080p' | '2k' | '4k';
  video_model?: VideoReplicationModel;
  depth_enabled?: boolean;
  genjutsu_preset_id?: string;
  genjutsu_max_credits?: number;
}

export interface VideoReplicationStartResult {
  billing_mode?: KagemaneBillingMode;
  pipeline?: 'hypit_video_replication' | 'genjutsu_video_replication';
  task_type?: string;
  task_id: string;
  /** Canvas identity only for a generation explicitly scoped to a project. */
  canvas_node_id: string | null;
  status: 'processing' | 'queued';
  source_duration_sec: number;
  effective_duration_sec: number;
  model: 'seedance-2.0' | 'hypit' | 'genjutsu';
  video_model?: VideoReplicationModel | null;
  resolution: '480p' | '768p' | '720p' | '1080p' | '2k' | '4k';
}

export interface CreateKagemaneTemplateRequest {
  project_id?: string | null;
  entry_point?: 'kagemane';
  client_request_id?: string;
  billing_mode?: KagemaneBillingMode;
  gift_max_credits?: number;
  template_id: string;
  template_version: number;
  source_node_id?: string | null;
  materials: { asset_id: string; role: VideoReplicationReferenceRole }[];
  resolution: '480p' | '768p' | '720p' | '1080p' | '2k' | '4k';
  video_model?: VideoReplicationModel;
  depth_enabled?: boolean;
  genjutsu_preset_id?: string;
  genjutsu_max_credits?: number;
  prompt?: string | null;
}

class VideoReplicationsApi extends ApiClient {
  async quote(videoModel: VideoReplicationModel, resolution: string, durationSec: number, billingMode: KagemaneBillingMode = 'dobi'): Promise<ApiResponse<KagemaneQuote>> {
    const query = new URLSearchParams({ video_model: videoModel, resolution, duration_sec: String(durationSec) });
    if (billingMode === 'monthly_gift') query.set('billing_mode', billingMode);
    return this.request(`/video-replications/quote?${query}`);
  }
  async genjutsuPresets(): Promise<ApiResponse<{ model: GenjutsuVideoModel; items: GenjutsuPreset[] }>> {
    return this.request('/video-replications/genjutsu/presets');
  }
  async genjutsuQuote(videoModel: GenjutsuVideoModel, resolution: string, durationSec: number): Promise<ApiResponse<GenjutsuQuote>> {
    const query = new URLSearchParams({ video_model: videoModel, resolution, duration_sec: String(durationSec) });
    return this.request(`/video-replications/genjutsu/quote?${query}`);
  }
  async gifts(): Promise<ApiResponse<KagemaneGifts>> {
    return this.request<KagemaneGifts>('/video-replications/gifts');
  }
  async openTemplateSource(projectId: string, templateId: string, version: number): Promise<ApiResponse<CanvasNodeRow>> {
    return this.request<CanvasNodeRow>(`/video-replications/templates/${encodeURIComponent(templateId)}/source`, {
      method: 'POST', body: JSON.stringify({ project_id: projectId, template_version: version }),
    });
  }

  async createFromTemplate(request: CreateKagemaneTemplateRequest): Promise<ApiResponse<VideoReplicationStartResult>> {
    return this.request<VideoReplicationStartResult>('/video-replications/from-template', {
      method: 'POST', body: JSON.stringify(request),
    });
  }
  async create(
    request: CreateVideoReplicationRequest,
  ): Promise<ApiResponse<VideoReplicationStartResult>> {
    const response = await this.request<VideoReplicationStartResult>('/video-replications', {
      method: 'POST',
      body: JSON.stringify(request),
    });
    if (response.success !== true || !response.data?.task_id || (request.project_id && !response.data.canvas_node_id?.trim())) {
      return {
        success: false,
        error: response.error ?? {
          code: 'VIDEO_REPLICATION_START_FAILED',
          message: translateClient('workflow', 'videoReplicationStartFailed'),
          action: 'retry',
          retryable: true,
        },
      };
    }
    return response;
  }
}

export const videoReplicationsApi = new VideoReplicationsApi();
